/**
 * Fetch Popular Subcategories API
 *
 * API function for fetching popular subcategories (categories with parentId)
 */

import { buildApiUrl, handleApiResponse } from '@/src/shared/api';
import type { Category } from '../types';

export interface FetchPopularSubcategoriesParams {
  limit?: number;
  useMock?: boolean;
}

/**
 * Fetch popular subcategories from the API
 * Returns only categories that have a parentId (are subcategories)
 *
 * @param params - Optional parameters for limiting results and mock mode
 * @returns Promise with array of popular subcategories
 */
export async function fetchPopularSubcategories(
  params: FetchPopularSubcategoriesParams = {},
): Promise<Category[]> {
  const { limit = 5, useMock = false } = params;

  // Use mock data for development
  if (useMock) {
    const { mockCategories } = await import('../model/mock/mock-categories');
    return new Promise((resolve) => {
      setTimeout(() => {
        // Filter subcategories (those with parentId) and return popular ones
        const subcategories = mockCategories
          .filter((cat) => cat.parentId !== null && cat.isPopular)
          .slice(0, limit);

        resolve(subcategories);
      }, 500);
    });
  }

  // Real API call
  // Note: Backend doesn't have a dedicated endpoint for popular subcategories
  // We use /categories/popular and filter on frontend, or fetch all subcategories
  // For MVP, we'll fetch popular categories and filter for subcategories
  const url = buildApiUrl(`/categories/popular?limit=${limit * 2}`);

  const response = await fetch(url);
  const data = await handleApiResponse<Category[]>(response);

  // Filter for subcategories only
  const subcategories = Array.isArray(data)
    ? data.filter((cat) => cat.parentId !== null).slice(0, limit)
    : [];

  return subcategories;
}
